import { useEffect, useState } from "react";
import { Calendar, MapPin, FileText, ExternalLink } from "lucide-react";
import { fetchEvents, fetchSetting } from "@/lib/api";
import { useLightbox, ClickableImage } from "@/components/Lightbox";

const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
    catch { return iso || ""; }
};

/*
 * Parish Calendar block — admin uploads either an image poster OR a PDF.
 * Image renders inline (clickable → lightbox). PDF renders an attractive dark
 * card that opens the file in a new tab. If neither exists, we show a subtle
 * "coming soon" placeholder so the page never has an empty rectangle.
 */
const CalendarBlock = () => {
    const [image, setImage] = useState(null);
    const [pdf, setPdf] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([
            fetchSetting("calendar_poster").catch(() => null),
            fetchSetting("calendar_pdf").catch(() => null),
        ]).then(([img, doc]) => {
            if (!alive) return;
            if (img?.url && img?.value) setImage(img.url);
            if (doc?.url && doc?.value) setPdf(doc.url);
        }).finally(() => alive && setLoaded(true));
        return () => { alive = false; };
    }, []);

    return (
        <section
            data-testid="parish-calendar-block"
            className="mt-2 mb-10"
        >
            <div className="flex items-end justify-between gap-2 mb-4">
                <div>
                    <span className="eyebrow">This Year</span>
                    <h2 className="heading-section mt-1">Parish Calendar</h2>
                </div>
            </div>

            {!loaded ? (
                <div
                    className="rounded-2xl border p-8 text-center italic"
                    style={{ background: "#2a1719", borderColor: "rgba(212, 175, 55, 0.3)", color: "#E8D9BF" }}
                >
                    Loading…
                </div>
            ) : image ? (
                <div
                    className="rounded-2xl overflow-hidden border shadow-xl"
                    style={{ borderColor: "rgba(212, 175, 55, 0.4)", background: "#000" }}
                >
                    <ClickableImage
                        src={image}
                        alt="Parish Calendar, Holy Trinity Kileleshwa"
                        className="w-full h-auto block cursor-zoom-in"
                        data-testid="parish-calendar-image"
                    />
                </div>
            ) : pdf ? (
                <a
                    href={pdf}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="parish-calendar-pdf-link"
                    className="group flex flex-col sm:flex-row items-start gap-4 rounded-2xl border p-6 shadow-xl transition-all hover:-translate-y-0.5 hover:shadow-2xl"
                    style={{ background: "#252525", borderColor: "rgba(212, 175, 55, 0.45)" }}
                >
                    <div
                        className="shrink-0 flex h-14 w-14 items-center justify-center rounded-full"
                        style={{ background: "rgba(212, 175, 55, 0.15)", border: "1px solid rgba(212, 175, 55, 0.5)" }}
                    >
                        <FileText size={26} style={{ color: "#D4AF37" }} />
                    </div>
                    <div className="flex-1">
                        <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "#D4AF37" }}>
                            PDF · Parish Calendar
                        </p>
                        <h3 className="mt-1 font-serif text-xl" style={{ color: "#F3EDE4" }}>
                            View Parish Calendar
                        </h3>
                        <p className="mt-2 text-sm" style={{ color: "#E8D9BF" }}>
                            Feast days, sacramental celebrations and parish events for the year. Opens in a new tab.
                        </p>
                    </div>
                    <ExternalLink size={16} style={{ color: "#D4AF37" }} className="mt-1 opacity-80 group-hover:opacity-100" />
                </a>
            ) : (
                <div
                    data-testid="parish-calendar-empty"
                    className="rounded-2xl border p-8 text-center"
                    style={{ background: "#252525", borderColor: "rgba(212, 175, 55, 0.35)", color: "#E8D9BF" }}
                >
                    <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "#D4AF37" }}>
                        Parish Calendar
                    </p>
                    <p className="mt-3 italic">Parish Calendar, coming soon.</p>
                </div>
            )}
        </section>
    );
};

const Events = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        fetchEvents()
            .then((d) => alive && setEvents(d))
            .catch(() => alive && setEvents([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const now = new Date().toISOString().slice(0, 10);
    const upcoming = events.filter((e) => (e.event_date || "") >= now).sort((a, b) => a.event_date.localeCompare(b.event_date));
    const past     = events.filter((e) => (e.event_date || "") <  now).sort((a, b) => b.event_date.localeCompare(a.event_date));
    const ordered  = [...upcoming, ...past];

    const posterItems = [];
    const posterIndexFor = {};
    ordered.forEach((e) => {
        if (e.poster_url) {
            posterIndexFor[e.id] = posterItems.length;
            posterItems.push({ src: e.poster_url, alt: `${e.title}, Holy Trinity Kileleshwa` });
        }
    });
    const { open, LightboxNode } = useLightbox(posterItems);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14" data-testid="events-page">
            <span className="eyebrow">Parish Life</span>
            <h1 className="heading-section mt-1">Events &amp; Calendar</h1>
            <p className="mt-3 max-w-2xl text-maroon-700/80">
                Feasts, celebrations and gatherings at Holy Trinity Kileleshwa.
            </p>
            <div className="gold-divider mt-5 opacity-60" />

            <div className="mt-8">
                <CalendarBlock />
            </div>

            <h2 className="heading-section mt-6" style={{ fontSize: "1.5rem" }}>Events</h2>
            <div className="gold-divider mt-3 mb-6 opacity-40 max-w-[160px]" />

            {loading ? (
                <p className="mt-4 italic text-maroon-700/70">Loading events…</p>
            ) : ordered.length === 0 ? (
                <p className="mt-4 italic text-maroon-700/70">No events posted yet. Check back soon.</p>
            ) : (
                <div className="mt-2 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {ordered.map((e) => {
                        const isPast = (e.event_date || "") < now;
                        return (
                            <article
                                key={e.id}
                                data-testid={`event-card-${e.id}`}
                                className="rounded-2xl overflow-hidden border shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                                style={{ background: "#252525", borderColor: "#900000", borderLeftWidth: 4 }}
                            >
                                {e.poster_url && (
                                    <button
                                        type="button"
                                        onClick={() => open(posterIndexFor[e.id])}
                                        aria-label={`Open poster for ${e.title}`}
                                        data-testid={`event-poster-${e.id}`}
                                        className="block w-full"
                                    >
                                        <img
                                            src={e.poster_url}
                                            alt={`Poster, ${e.title}`}
                                            loading="lazy"
                                            className="w-full h-56 object-cover cursor-zoom-in"
                                        />
                                    </button>
                                )}
                                <div className="p-5">
                                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold" style={{ color: "#D4AF37" }}>
                                        <Calendar size={12} /> {formatDate(e.event_date)}
                                        {isPast && <span className="ml-2 rounded-full px-2 py-0.5 text-[9px]" style={{ background: "rgba(255,255,255,0.1)", color: "#F3EDE4" }}>Past</span>}
                                    </div>
                                    <h2 className="mt-2 font-serif text-xl text-cream-50">{e.title}</h2>
                                    {e.venue && (
                                        <p className="mt-1 text-sm flex items-center gap-1" style={{ color: "#E8D9BF" }}>
                                            <MapPin size={12} /> {e.venue}
                                        </p>
                                    )}
                                    {e.description && (
                                        <>
                                            <div className="gold-divider my-3 opacity-30" />
                                            <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "#E8D9BF" }}>{e.description}</p>
                                        </>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
            {LightboxNode}
        </div>
    );
};

export default Events;
